import { Modal } from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { IntegrationAdapter, IntegrationConfig, IntegrationConfigTab } from '../../types';
import ConnectionTab from './ConnectionTab';
import { PanelPosition, TabButton, TabList, TitleWrapper } from './styled';

type PanelTab = 'connection' | 'config';

const TABS: { id: PanelTab; label: string }[] = [
  { id: 'connection', label: 'Connection' },
  { id: 'config', label: 'Config' },
];

interface IntegrationPanelProps {
  config: IntegrationConfig;
  adapter: IntegrationAdapter;
  configTab?: IntegrationConfigTab;
  open: boolean;
  onClose: () => void;
}

const IntegrationPanel = ({ config, adapter, configTab, open, onClose }: IntegrationPanelProps) => {
  const isConnected = adapter.status === 'active';
  const { ConfigView } = config;
  const hasTabs = !!ConfigView && !!configTab;
  const [activeTab, setActiveTab] = useState<PanelTab>('connection');
  const isConfigTab = hasTabs && activeTab === 'config';

  useEffect(() => {
    if (!open) setActiveTab('connection');
  }, [open]);

  useEffect(() => {
    if (isConfigTab) configTab?.onActivate?.();
  }, [isConfigTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (adapter.isDirty || adapter.isEditing) adapter.onDiscard();
    if (configTab?.isDirty || configTab?.isEditing) configTab.onDiscard();
    onClose();
  };

  const getActions = () => {
    if (isConfigTab && configTab) {
      return configTab.isEditing
        ? {
            primaryAction: {
              content: 'Save settings',
              onAction: configTab.onSave,
              disabled: !configTab.isDirty,
              loading: configTab.isSaving,
            },
            secondaryActions: [{ content: 'Cancel', onAction: configTab.onDiscard }],
          }
        : {
            primaryAction: { content: 'Edit', onAction: configTab.onEdit },
          };
    }
    if (isConnected) {
      if (adapter.isEditing) {
        return {
          primaryAction: { content: 'Save', onAction: adapter.onSave, disabled: !adapter.canSave, loading: adapter.isSaving },
          secondaryActions: [{ content: 'Cancel', onAction: adapter.onDiscard }],
        };
      }
      return {
        primaryAction: {
          content: 'Disconnect',
          onAction: adapter.onDisconnect,
          destructive: true,
          loading: adapter.isDisconnecting,
        },
        secondaryActions: adapter.onEdit ? [{ content: 'Edit', onAction: adapter.onEdit }] : undefined,
      };
    }
    return {
      primaryAction: { content: 'Save', onAction: adapter.onSave, disabled: !adapter.canSave, loading: adapter.isSaving },
      secondaryActions: [
        ...(adapter.isDirty ? [{ content: 'Discard', onAction: adapter.onDiscard }] : []),
        { content: 'Cancel', onAction: handleClose },
      ],
    };
  };

  const panelTitle = `${isConnected ? 'Manage' : 'Connect'} ${config.title}`;

  const titleNode = (
    <TitleWrapper $hasTabs={hasTabs}>
      <div>{panelTitle}</div>
      {hasTabs && (
        <TabList role="tablist">
          {TABS.map(({ id, label }) => (
            <TabButton
              key={id}
              type="button"
              role="tab"
              aria-selected={activeTab === id}
              $active={activeTab === id}
              onClick={() => setActiveTab(id)}
            >
              {label}
            </TabButton>
          ))}
        </TabList>
      )}
    </TitleWrapper>
  );

  return (
    <>
      {open && <PanelPosition />}
      <Modal open={open} onClose={handleClose} title={titleNode} {...getActions()}>
        <Modal.Section>
          {isConfigTab && ConfigView && configTab ? (
            <ConfigView config={configTab} />
          ) : (
            <ConnectionTab config={config} adapter={adapter} />
          )}
        </Modal.Section>
      </Modal>
    </>
  );
};

export default IntegrationPanel;
